// Client layer (TS-2.5.03). The wizard's first step - a REAL file input (accessible via keyboard
// and screen reader out of the box) with drag-and-drop layered on as a pure enhancement that never
// becomes the only way to choose a file, per this batch's upload-UX requirement. An optional
// source-profile code travels as a query parameter alongside the multipart body (ingestion.ts's
// own header explains why file + scalar fields cannot share one Swashbuckle-described body).
import { useRef, useState } from "react";
import type { DragEvent } from "react";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { strings } from "../../strings";

export interface ImportUploadFormProps {
  readonly submitting: boolean;
  readonly onSubmit: (file: File, sourceProfileCode: string | undefined) => void;
}

const S = strings.import.upload;

export function ImportUploadForm({ submitting, onSubmit }: ImportUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [sourceProfileCode, setSourceProfileCode] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    const dropped = event.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }

  return (
    <form
      className="eg-import-upload"
      onSubmit={(event) => {
        event.preventDefault();
        if (file) onSubmit(file, sourceProfileCode.trim().length > 0 ? sourceProfileCode.trim() : undefined);
      }}
    >
      <FormField id="eg-import-file" label={S.fileLabel} required>
        <div
          className={`eg-import-upload__dropzone${dragOver ? " eg-import-upload__dropzone--active" : ""}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {/* Not natively `required` - jsdom's constraint-validation support for type="file" is
             unreliable, and the submit button below is already disabled with no file chosen, so
             native validation would be redundant with that guard, not a second safety net. */}
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            {...formFieldControlProps("eg-import-file")}
          />
          {file && <p>{file.name}</p>}
        </div>
      </FormField>

      <FormField id="eg-import-source-profile" label={S.sourceProfile}>
        <input
          type="text"
          value={sourceProfileCode}
          onChange={(event) => setSourceProfileCode(event.target.value)}
          {...formFieldControlProps("eg-import-source-profile")}
        />
      </FormField>

      <button type="submit" disabled={submitting || !file}>
        {submitting ? S.uploading : S.submit}
      </button>
    </form>
  );
}
